import { Check, Copy } from 'iconoir-react';
import * as React from 'react';

import { Button } from '@savgroup-front-common/primitive/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@savgroup-front-common/primitive/ui/tooltip';
import { useCopyToClipboard } from '@savgroup-front-common/primitive/hooks/use-copy-to-clipboard';
import { cn } from '@savgroup-front-common/primitive/lib/utils';

export function CopyButton({
  value,
  label = 'Copy to clipboard',
  children,
  onClick,
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'value' | 'className'> & {
  value: string;
  label?: string;
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
}) {
  const { copy, isCopied } = useCopyToClipboard();

  const copyButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          data-slot="copy-button"
          aria-label={label}
          className={cn(
            'revers-copy-button',
            'absolute top-1/2 right-0 -translate-y-1/2 opacity-0 transition-opacity group-hover/copy-button-wrapper:opacity-100 focus-visible:opacity-100',
          )}
          onClick={onClick}
          {...props}
        >
          {isCopied ? (
            <Check className="size-3.5" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );

  return React.cloneElement(children, {
    'data-slot': 'copy-button-wrapper',
    className: cn(
      'revers-copy-button-wrapper',
      'group/copy-button-wrapper relative -my-0.5 -ml-1 w-fit cursor-pointer rounded-sm py-0.5 pl-1 pr-7 hover:bg-surface-primary-subtle-hover hover:stext-base-default',
      children.props.className,
    ),
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      void copy(value);
      children.props.onClick?.(event);
    },
    children: (
      <>
        {children.props.children}
        {copyButton}
      </>
    ),
  } as React.HTMLAttributes<HTMLElement>);
}
